// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Slider as ReactSlider,
  Rail as Rails,
  Handles,
  Tracks,
  HandleItem,
} from 'react-compound-slider';
import { cx } from '@linaria/core';
import styles from './styles';
import { Rail, Track, Handle } from './common';
import { DualSliderProps } from './types';

const wrapToObject = (valueArray: ReadonlyArray<number>, rtl: boolean) => ({
  lower: valueArray[rtl ? 1 : 0],
  upper: valueArray[rtl ? 0 : 1],
});

const truncateNumber = (value: number, step: number) => {
  return (Number.isInteger(step) ? value : value.toFixed(2)).toString();
};

const defaultFormat = (value: { lower: number; upper: number }, step?: number): string => {
  return `${truncateNumber(value.lower, step)} - ${truncateNumber(value.upper, step)}`;
};

const preventCrossingWithDistance =
  (distanceBtwKnobs = 1, rtl = false) =>
  (curr: HandleItem[], next: HandleItem[]) => {
    // Looping through handles
    for (let i = 0; i < curr.length; i += 1) {
      if (curr[i].key !== next[i].key) {
        return curr;
      }
      // prevent crossing
      // In incoming update if distance between handles is less than or equal to distanceBtwKnobs, return current state
      if (rtl) {
        // in rtl, handles positions are inter-changed
        if (next[i + 1] && next[i].val <= next[i + 1].val + (distanceBtwKnobs - 1)) {
          return curr;
        }
      } else if (next[i + 1] && next[i].val >= next[i + 1].val - (distanceBtwKnobs - 1)) {
        return curr;
      }
    }
    return next;
  };

const DualSlider = (props: DualSliderProps) => {
  const {
    id,
    min = 0,
    max = 100,
    step = 1,
    minSeparation = step,
    mode: explicitMode,
    valueLower,
    valueUpper,
    width = '100%',
    label,
    showValue = true,
    onUpdate,
    onChange,
    formatValue = defaultFormat,
    disabled,
    rtl = false,
    handleNames,
    handleIds,
    className,
    accentColor,
    ...restProps
  } = props;

  const domain = useMemo(() => [min, max], [min, max]);

  const values = useMemo(
    () => (rtl ? [valueUpper, valueLower] : [valueLower, valueUpper]),
    [rtl, valueLower, valueUpper]
  );

  const [localValues, setLocalValues] = useState(values as ReadonlyArray<number>);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const handleUpdate = useCallback(
    (updatedValue: ReadonlyArray<number>) => {
      setLocalValues(updatedValue);
      if (onUpdate) {
        onUpdate(wrapToObject(updatedValue, rtl));
      }
    },
    [onUpdate, rtl]
  );

  const handleChange = useCallback(
    (updatedValue: ReadonlyArray<number>) => {
      onChange(wrapToObject(updatedValue, rtl));
    },
    [onChange, rtl]
  );

  const mode = useMemo(
    () => explicitMode || preventCrossingWithDistance(minSeparation, rtl),
    [explicitMode, minSeparation, rtl]
  );

  return (
    <div
      data-disabled={disabled ? '' : undefined}
      className={cx(styles.container, className)}
      style={{ width }}
      {...restProps}
    >
      <div className={styles.labels}>
        <label htmlFor={id}>{label}</label>
        {showValue ? (
          <div aria-valuetext={formatValue(wrapToObject(localValues, rtl), step)}>
            {formatValue(wrapToObject(localValues, rtl), step)}
          </div>
        ) : null}
      </div>

      <div className={styles.wrapper}>
        <ReactSlider
          mode={mode}
          step={step}
          disabled={disabled}
          domain={domain}
          values={values}
          reversed={rtl}
          onUpdate={handleUpdate}
          onChange={onChange && handleChange}
        >
          <Rails>
            {({ getRailProps }) => <Rail getRailProps={getRailProps} disabled={disabled} />}
          </Rails>

          <Tracks left={false} right={false}>
            {({ tracks, getTrackProps }) => (
              <div>
                {tracks.map(({ id, source, target }) => (
                  <Track
                    key={id}
                    source={source}
                    target={target}
                    disabled={disabled}
                    getTrackProps={getTrackProps}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            )}
          </Tracks>

          <Handles>
            {({ handles, getHandleProps }) => (
              <div className={styles.handleWrapper}>
                {handles.map((handle, index) => (
                  <Handle
                    key={handle.id}
                    handle={handle}
                    domain={domain}
                    disabled={disabled}
                    getHandleProps={getHandleProps}
                    accessibilityText={
                      handleNames ? handleNames[rtl ? 1 - index : index] : undefined
                    }
                    data-testid={handleIds ? handleIds[rtl ? 1 - index : index] : undefined}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            )}
          </Handles>
        </ReactSlider>
      </div>
    </div>
  );
};

export default memo(DualSlider);
